import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useIsOsMode, useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import { useWindowId } from "@/components/ui/os-layout/OsWindow";
import { useSetWindowParams, useSetWindowTitle } from "@/components/ui/os-layout";
import {
  buildCalculatorsOpenWindowConfig,
  buildCalculatorsPath,
  calculatorsWindowTitle,
  type CalculatorsLocation,
} from "@/navigation/apps/calculatorsLocation";

export type LaunchCalculatorOptions = CalculatorsLocation & {
  /**
   * When true (Calculators hub inside an OS window), update the current window
   * instead of opening another. Default false — opens a dedicated OS window.
   */
  inPlace?: boolean;
};

/** Build a calculators URL (classic router). */
export function buildLaunchCalculatorUrl(
  options: LaunchCalculatorOptions = {},
): string {
  return buildCalculatorsPath(options);
}

export type LaunchCalculatorFn = (options: LaunchCalculatorOptions) => void;

/** Imperative navigation to Calculators — new OS window by default. */
export function useLaunchCalculator(defaults?: { inPlace?: boolean }): LaunchCalculatorFn {
  const navigate = useNavigate();
  const isOsMode = useIsOsMode();
  const windowId = useWindowId();
  const setWindowParams = useSetWindowParams();
  const setWindowTitle = useSetWindowTitle();
  const actions = useOsActionsOptional();

  return useCallback(
    (options: LaunchCalculatorOptions) => {
      const { inPlace: inPlaceOpt, ...loc } = options;
      const inPlace = inPlaceOpt ?? defaults?.inPlace ?? false;
      const path = buildCalculatorsPath(loc);
      const title = calculatorsWindowTitle(loc);
      const osActive = isOsMode || Boolean(windowId);

      if (osActive && inPlace && windowId) {
        navigate(path);
        const { params } = buildCalculatorsOpenWindowConfig(loc);
        setWindowParams(params);
        setWindowTitle(title);
        return;
      }

      if (osActive && !inPlace && actions?.openWindow) {
        const config = buildCalculatorsOpenWindowConfig(loc);
        actions.openWindow({
          appId: config.appId,
          title: config.title,
          params: config.params,
          focus: true,
        });
        return;
      }

      navigate(path);
    },
    [
      defaults?.inPlace,
      isOsMode,
      windowId,
      navigate,
      setWindowParams,
      setWindowTitle,
      actions,
    ],
  );
}
